// src/components/HomeFeed.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";


import { Box, CircularProgress, Typography } from "@mui/material";
import api from "../api/axiosConfig";

import FeedCard from "./FeedCard";
import MomentWindowCard from "./MomentWindowCard";
import MomentEndedCard from "./MomentEndedCard";
import ShoutoutCard from "./ShoutoutCard";
import FeedSearchBar from "./FeedSearchBar";
import MyPulseSection from "./MyPulseSection";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";

// ✅ NEW (Slide transition)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});






export default function HomeFeed({ userId }) {
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [momentWindows, setMomentWindows] = useState([]);
  const [endCards, setEndCards] = useState([]);
  const [shoutouts, setShoutouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [query, setQuery] = useState("");

  // ✅ NEW: fullscreen post viewer state

   const [openPost, setOpenPost] = useState(null);
  const handleOpenPost = (post) => setOpenPost(post);
  const handleClosePost = () => setOpenPost(null);

  const [pendingPosts, setPendingPosts] = useState([]);
const [showNewBanner, setShowNewBanner] = useState(false);
const [hiddenPostIds, setHiddenPostIds] = useState(() => new Set());


// used to detect “new posts”

const [feedInitialized, setFeedInitialized] = useState(false);





  /* ===============================
     LOAD POSTS
  ================================ */
  const loadPosts = useCallback(async () => {
  try {
    const res = await api.get(`/feed/${userId}`, { timeout: 3000 });
    const incomingRaw = Array.isArray(res.data.feed) ? res.data.feed : [];
const incoming = incomingRaw.filter((p) => !hiddenPostIds.has(p.id));


    // first load: set normally
    if (!feedInitialized) {
  setPosts(incoming);
  setFeedInitialized(true);
  localStorage.setItem("feed-cache", JSON.stringify(incoming));
  return;
}

    

    // find new posts that are not already in current posts
    const currentIds = new Set(posts.map((p) => p.id));
    const newOnes = incoming.filter((p) => !currentIds.has(p.id));

    if (newOnes.length > 0) {
      // buffer them (avoid duplicates if multiple polls)
      setPendingPosts((prev) => {
        const prevIds = new Set(prev.map((p) => p.id));
        const merged = [...newOnes.filter((p) => !prevIds.has(p.id)), ...prev];
        return merged;
      });

      setShowNewBanner(true);
     
    }

    // no new posts → do nothing (keeps feed stable)
  } catch (err) {
    console.warn("Feed refresh failed — keeping old feed");
  }
}, [userId, posts, feedInitialized, hiddenPostIds]);



useEffect(() => {
  if (pendingPosts.length === 0) setShowNewBanner(false);
}, [pendingPosts.length]);




useEffect(() => {
  if (!userId) return;

  const firstLoad = async () => {
    setLoading(true);
    await loadPosts();
    setLoading(false);
    setHasLoadedOnce(true);
  };

  if (!hasLoadedOnce) {
    firstLoad();
  }
}, [userId, hasLoadedOnce, loadPosts]);

/* ===============================
   ACTIVE MOMENT WATCHER ✅ (no refresh needed)
================================ */
useEffect(() => {
  if (!userId) return;

  let cancelled = false;
  let inFlight = false;

  const check = async () => {
    if (cancelled || inFlight) return;
    inFlight = true;

    try {
      const res = await api.get(`/moments/active/${userId}`);
      const momentId = res.data?.momentId ? String(res.data.momentId) : null;
      if (!momentId) return;

      // ✅ Always allow a real active moment to open
      sessionStorage.removeItem("moment_suppressed");

      const activeId = sessionStorage.getItem("active_moment_id");
      if (activeId === momentId) return;

      sessionStorage.setItem("active_moment_id", momentId);
      navigate(`/moment/${momentId}`);
    } catch (e) {
      // ignore
    } finally {
      inFlight = false;
    }
  };

  // run immediately + poll
  check();
  const interval = setInterval(check, 1000);

  // check again when user returns to tab
  const onFocus = () => check();
  window.addEventListener("focus", onFocus);

  return () => {
    cancelled = true;
    clearInterval(interval);
    window.removeEventListener("focus", onFocus);
  };
}, [userId, navigate]);







  useEffect(() => {
  const handleScrollToPost = (e) => {
    const { postId } = e.detail;
    const el = document.getElementById(`post-${postId}`);

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  window.addEventListener("scroll-to-post", handleScrollToPost);

  return () => {
    window.removeEventListener("scroll-to-post", handleScrollToPost);
  };
}, []);


  const loadMomentWindows = useCallback(async () => {
    try {
      const res = await api.get(`/moment/windows/${userId}`);
      setMomentWindows(res.data.windows || []);
    } catch {}
  }, [userId]);

  const loadEndCards = useCallback(async () => {
    try {
      const res = await api.get(`/moment/end-cards/${userId}`);
      setEndCards(res.data.endCards || []);
    } catch {}
  }, [userId]);

  const loadShoutouts = useCallback(async () => {
    try {
      const res = await api.get(`/shoutouts/feed/${userId}`);
      setShoutouts(res.data.shoutouts || []);
    } catch {}
  }, [userId]);

  /* ===============================
     INITIAL LOAD
  ================================ */
  useEffect(() => {
    if (!userId) return;

    loadPosts();
    loadMomentWindows();
    loadEndCards();
    loadShoutouts();

    const interval = setInterval(() => {
      loadPosts();
      loadMomentWindows();
      loadEndCards();
      loadShoutouts();
    }, 3000);

    return () => clearInterval(interval);
  }, [
    userId,
    loadPosts,
    loadMomentWindows,
    loadEndCards,
    loadShoutouts,
  ]);

  /* ===============================
     LOADING STATE
  ================================ */
  
useEffect(() => {
  const cached = localStorage.getItem("feed-cache");
  if (cached) {
    setPosts(JSON.parse(cached));
    setHasLoadedOnce(true);
  }
}, []);

useEffect(() => {
  if (posts.length > 0) {
    localStorage.setItem("feed-cache", JSON.stringify(posts));
  }
}, [posts]);

  /* ===============================
     MERGE FEED ITEMS  ✅ MUST BE HERE
  ================================ */
  const unified = [
    ...posts.map((p) => ({
      type: "post",
      createdAt: p.createdAt,
      data: p,
    })),
    ...momentWindows.map((m) => ({
      type: "momentWindow",
      createdAt: m.createdAt,
      data: m,
    })),
    ...endCards.map((e) => ({
      type: "momentEnded",
      createdAt: e.createdAt,
      data: e,
    })),
    ...shoutouts.map((s) => ({
      type: "shoutout",
      createdAt: s.createdAt,
      data: s,
    })),
  ].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() -
      new Date(a.createdAt).getTime()
  );

  /* ===============================
   🔍 FEED SEARCH FILTER
================================ */

const q = query.trim().toLowerCase();

const filtered = !q
  ? unified
  : unified.filter((item) => {
      if (item.type !== "post") return true;

      const p = item.data;
      const username = (p.username || "").toLowerCase();
      const caption = (p.caption || "").toLowerCase();
      const courseCode = (p.courseCode || "").toLowerCase();

      const normalized = q.startsWith("#") ? q.slice(1) : q;
      const normalizedAt = normalized.startsWith("@")
        ? normalized.slice(1)
        : normalized;

      return (
        username.includes(normalizedAt) ||
        caption.includes(normalized) ||
        courseCode.includes(normalized)
      );
    });


  /* ===============================
     FINAL RENDER  ✅ ONLY ONE RETURN
  ================================ */
  return (
  <Box>
    <MyPulseSection userId={userId} />

    <FeedSearchBar
  value={query}
  onChange={setQuery}
  onTag={(t) => setQuery(t)}
/>

<Dialog
        fullScreen
        open={!!openPost}
        onClose={handleClosePost}
        TransitionComponent={Transition}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "#fff",
            display: "flex",
            justifyContent: "flex-end",
            px: 1,
            py: 1,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <IconButton onClick={handleClosePost} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 1.5, py: 1.5 }}>
          {openPost ? <FeedCard item={openPost} fullscreen /> : null}
        </Box>
      </Dialog>


    {/* 🔹 FEED */}
    <Box sx={{ px: 1.5 }}>

      {showNewBanner && pendingPosts.length > 0 && (
  <Box
    onClick={() => {
      setPosts((prev) => [...pendingPosts, ...prev]);
      setPendingPosts([]);
      setShowNewBanner(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }}
    sx={{
      position: "sticky",
      top: 64,
      zIndex: 9,
      mx: "auto",
      mb: 1.5,
      px: 2,
      py: 1,
      borderRadius: "999px",
      width: "fit-content",
      backgroundColor: "#111827",
      color: "white",
      fontWeight: 700,
      fontSize: "0.85rem",
      cursor: "pointer",
      boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
      userSelect: "none",
    }}
  >
    New posts available · Tap to load ({pendingPosts.length})
  </Box>
)}



      {!hasLoadedOnce && loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress size={28} />
        </Box>
       ) : filtered.length === 0 ? ( 
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Nothing here yet
          </Typography>
          <Typography color="text.secondary">
            Turn on location to see what’s happening nearby
          </Typography>
        </Box>

        

      ) : (

        
         filtered.map((item, index) => { 
          switch (item.type) {
            case "post":
              return (
                <FeedCard
                  key={`post-${item.data.id}`}

                  item={item.data}
                  onOpen={() => setOpenPost(item.data)}   // ✅ ADD THIS LINE
                 onDelete={(id) => {
                  setHiddenPostIds((prev) => {
                    const next = new Set(prev);
                    next.add(id);
                    return next;
                  });

                  setPosts((prev) => prev.filter((p) => p.id !== id));
                }}

                />
              );

              case "momentWindow":
                return (
                  <MomentWindowCard
                    key={`mw-${item.data.sessionId}`}
                    data={item.data}
                  />
                );

              case "momentEnded":
                return (
                  <MomentEndedCard
                    key={`ended-${item.data.sessionId}`}
                    data={item.data}
                  />
                );

              case "shoutout":
                return (
                  <ShoutoutCard
                    key={`shoutout-${item.data.id}-${index}`}
                    data={item.data}
                  />
                );

              default:
                return null;
            }
          })
        )}
      </Box>
    </Box>
  );
}
